/** @prettier */

import update from 'immutability-helper';
import {useCallback, useEffect, useState, useRef} from 'react';
import type {XYCoord} from 'react-dnd';
import {useDrop} from 'react-dnd';
import CircularProgress from '@mui/material/CircularProgress';
import {default as MuiBox} from '@mui/material/Box';
import Stack from '@mui/material/Stack';

import {Box as DnDBox} from './Box';
import type {DragItem} from './interfaces';
import {ItemTypes} from './ItemTypes';
import {useDragPreview} from './hooks';
import DragPreviewPresenter from './presenter';
//Style import
import '../../../css/common/common.css';
//type import
import {layoutUnitListType} from '../../../Type/TypeDef';

import {limit, iconBlack} from '../../../define';

import {useTranslation} from 'react-i18next';

export interface ContainerProps {
  /** 背景画像 */
  image: string | null;
  /** グループに登録されている空調機情報（修正前） */
  groupUnitInfo: {
    'unit-id': string;
    'unit-name': string;
    'x-coordinate': string;
    'y-coordinate': string;
  }[];
  /** グループに登録されている空調機情報（set関数） */
  setGroupUnitInfo: React.Dispatch<
    React.SetStateAction<
      {
        'unit-id': string;
        'unit-name': string;
        'x-coordinate': string;
        'y-coordinate': string;
      }[]
    >
  >;
  unitList?: layoutUnitListType;
}
interface IconListIF {
  [key: string]: {top: number; left: number; unitName: string};
}
/**
 * ドラッグ・ドロップ画面
 * @param props props
 * @returns JSX
 *
 * 参考
 * - https://qiita.com/shungiku/items/e395251d7ee3f3d220c1
 * - https://qiita.com/s_kido14/items/d506805b40da3eb514ad
 */
const DragDrop = (props: ContainerProps) => {
  const trans = useTranslation().t; //翻訳関数
  const imageElement = useRef<HTMLImageElement>(null); //画像出力用
  const layoutElement = useRef<HTMLDivElement>(null); //アイコン表示用要素
  const iconNameElement = useRef<HTMLDivElement>(null); //機種アイコン+ユニット名表示要素

  const [iconList, setIconList] = useState<null | IconListIF>(null); //アイコンと表示位置のリスト

  //アイコンと表示位置のリストを作成
  useEffect(() => {
    //要素を取得できない場合
    if (layoutElement.current === null) return;

    //初期位置アイコン用変数
    const layoutWidth = layoutElement.current.offsetWidth; //レイアウト画像の幅
    const layoutHeight = layoutElement.current.offsetHeight; //レイアウト画像の高さ
    const displaceX = 10; //初期位置アイコンが複数あった場合のX軸ずらす距離
    const displaceY = 17; //初期位置アイコンが複数あった場合のY軸ずらす距離
    const maxWidthNum = 5; //初期位置アイコンを横方向に並べる最大数
    const maxHeightNum = 5; //初期位置アイコンを縦方向に並べる最大数

    /**
     * アイコンの表示する横位置を算出
     * @param unitPosX X座標（相対座標）
     * @param width 基準となる横幅
     * @returns アイコンの表示する左上からの位置
     */
    const iconPosX = (unitPosX: number, width: number) => {
      if (width === undefined) return 0;
      return (width / limit.LAYOUT_SPLIT_NUM) * unitPosX;
    };
    /**
     * アイコンの表示する縦位置を算出
     * @param unitPosY Y座標（相対座標）
     * @param height 基準となる高さ
     * @returns アイコンの表示する左上からの位置
     */
    const iconPosY = (unitPosY: number, height: number) => {
      if (height === undefined) return 0;
      return (height / limit.LAYOUT_SPLIT_NUM) * unitPosY;
    };

    let currentPosX = 0; //X軸初期位置
    let currentPosY = 0; //Y軸初期位置
    const list = props.groupUnitInfo.reduce((result, unitInfo) => {
      const xCoordinate = Number(unitInfo['x-coordinate']);
      const yCoordinate = Number(unitInfo['y-coordinate']);

      if (xCoordinate < 0 || yCoordinate < 0) {
        //初期位置のアイコンの場合
        result[unitInfo['unit-id']] = {
          top: iconPosY(currentPosY, layoutHeight),
          left: iconPosX(currentPosX, layoutWidth),
          unitName: unitInfo['unit-name'],
        };
        //次の初期位置アイコンは右にずらす
        currentPosX += displaceX;
        //横に並べる数が最大を超えた場合は下にずらす
        if (currentPosX > displaceX * maxWidthNum) {
          currentPosX = 0;
          currentPosY += displaceY;
          //縦に並べる数が最大を超えた場合は右上に戻す
          if (currentPosY > displaceY * maxHeightNum) {
            currentPosY = 0;
          }
        }
      } else {
        result[unitInfo['unit-id']] = {
          top: iconPosY(yCoordinate, layoutHeight),
          left: iconPosX(xCoordinate, layoutWidth),
          unitName: unitInfo['unit-name'],
        };
      }
      return result;
    }, {} as IconListIF);
    setIconList(list);
  }, [layoutElement, props.groupUnitInfo]);

  //レイアウト画像を表示
  useEffect(() => {
    //画像・要素を取得できない場合
    if (imageElement.current === null || props.image === null) return;

    imageElement.current.src = props.image;
  }, [imageElement, props.image]);

  //アイコンの座標を更新
  useEffect(() => {
    //要素を取得できない場合
    if (iconList === null || layoutElement.current === null) return;

    const layoutWidth = layoutElement.current.offsetWidth; //レイアウト画像の幅
    const layoutHeight = layoutElement.current.offsetHeight; //レイアウト画像の高さ

    props.setGroupUnitInfo(
      Object.entries(iconList).map(([unitID, data]) => {
        return {
          'unit-id': unitID,
          'unit-name': data.unitName,
          'x-coordinate': String(
            Math.round(data.left * (limit.LAYOUT_SPLIT_NUM / layoutWidth))
          ),
          'y-coordinate': String(
            Math.round(data.top * (limit.LAYOUT_SPLIT_NUM / layoutHeight))
          ),
        };
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iconList, layoutElement]);
  const moveBox = useCallback(
    (id: string, left: number, top: number, title: string) => {
      setIconList(
        update(iconList, {
          [id]: {
            $merge: {left, top, unitName: title},
          },
        })
      );
    },
    [iconList, setIconList]
  );

  //ドロップ制御
  const [, drop] = useDrop(
    () => ({
      accept: ItemTypes.BOX, //結びつけるドラッグ制御のID
      drop(item: DragItem, monitor) {
        //画像・要素を取得できない場合
        if (
          layoutElement.current === null ||
          iconNameElement.current === null
        ) {
          return undefined;
        }
        const layoutWidth = layoutElement.current.offsetWidth; //レイアウト画像の幅
        const layoutHeight = layoutElement.current.offsetHeight; //レイアウト画像の高さ
        const iconNameWidth = iconNameElement.current.offsetWidth; //機種アイコン＋ユニット名の幅
        const iconNameHeight = iconNameElement.current.offsetHeight; //機種アイコン＋ユニット名の高さ
        const delta = monitor.getDifferenceFromInitialOffset() as XYCoord; //ドラッグ前後の差分
        let left = Math.round(Number(item.left) + Number(delta.x));
        let top = Math.round(Number(item.top) + Number(delta.y));
        //ドロップ位置が枠外の場合、元の位置に戻す
        if (
          left < 0 ||
          left > layoutWidth - iconNameWidth ||
          top < 0 ||
          top > layoutHeight - iconNameHeight
        ) {
          left = Math.round(Number(item.left));
          top = Math.round(Number(item.top));
        }
        const title = item.unitName;
        moveBox(item.id, left, top, title);
        return undefined;
      },
    }),
    [moveBox] //依存関係
  );

  /**
   * ドラッグ・ドロップ処理を表示
   * @returns JSX
   */
  const DragPreview = () => {
    const {isDragging, unitID, ...dragProps} = useDragPreview();
    //ドラッグ中じゃない時はプレビューのコンポーネントを返さない
    if (!isDragging) {
      return <></>;
    }
    return (
      <DragPreviewPresenter
        {...dragProps}
        iconSrc={unitIconSrc(unitID, props.unitList)}
      />
    );
  };

  /**
   * ユニットアイコンのソース
   * @param unitID ユニットID
   * @param unitList ユニットリスト
   * @returns ユニットのタイプに応じたアイコンのソース
   */
  const unitIconSrc = (
    unitID: string,
    unitList?: {
      unitID: string;
      unitType: layoutUnitListType[0]['unitType'];
    }[]
  ) => {
    let unitType: layoutUnitListType[0]['unitType'] | '';
    if (unitList !== undefined) {
      const result = unitList.find(info => info.unitID === unitID);
      unitType = result === undefined ? '' : result.unitType;
    }
    switch (unitType) {
      case 'AC':
        return iconBlack.AC_ON.src;
      case 'FDT':
        return iconBlack.FDT_ON.src;
      case 'FDTW':
        return iconBlack.FDTW_ON.src;
      case 'FDTS':
        return iconBlack.FDTS_ON.src;
      case 'FDE':
        return iconBlack.FDE_ON.src;
      case 'FDF':
        return iconBlack.FDF_ON.src;
      case 'FDFW':
        return iconBlack.FDFW_ON.src;
      case 'FDK':
        return iconBlack.FDK_ON.src;
      case 'FDU':
        return iconBlack.FDU_ON.src;
      case 'VENT':
        return iconBlack.VENTILATOR_UNIT_ON.src;
      case 'LIGHT':
        return iconBlack.LIGHT_UNIT_ON.src;
      case 'Hyozan':
        return iconBlack.FREEZER_ON.src;
    }
    return iconBlack.AC_ON.src;
  };

  return (
    <MuiBox ref={layoutElement}>
      <MuiBox
        ref={drop}
        className="groupSetting__contents__layoutContents__layoutBox--setting">
        {/** 画像 */}
        <img
          className="groupSetting__contents__layoutContents__image"
          ref={imageElement}
          alt={`${trans('Container.image.alternativeText.groupLayoutImage')}`}
        />
        {/** ドラッグ・ドロップ処理を表示 */}
        {iconList === null ? (
          <MuiBox className="groupSetting__contents__layoutContents__layoutBox--setting__progress">
            <CircularProgress size={50} />
          </MuiBox>
        ) : (
          Object.keys(iconList).map((key, i) => {
            const {left, top, unitName} = iconList[key];
            return (
              <DnDBox
                key={key}
                id={key}
                left={left}
                top={top}
                unitName={unitName}>
                <Stack
                  alignItems="center"
                  id={`mv-layoutUnit${i}`}
                  ref={iconNameElement}>
                  <MuiBox
                    className="groupSetting__contents__layoutContents__layoutBox--setting__icon--normal"
                    style={{
                      backgroundImage: `url(${unitIconSrc(
                        key,
                        props.unitList
                      )})`,
                    }}></MuiBox>
                  <MuiBox
                    title={unitName}
                    className="groupSetting__contents__layoutContents__layoutBox--setting__text--normal">
                    {unitName}
                  </MuiBox>
                </Stack>
              </DnDBox>
            );
          })
        )}
        {/** ドラッグ・ドロップ処理のプレビューを表示 */}
        <DragPreview />
      </MuiBox>
    </MuiBox>
  );
};
export default DragDrop;
